import { html, LitElement, unsafeCSS } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { handleKeyboardTrigger } from '../../base/package/util/a11y';
import { isMobileWidth } from '../../base/package/util/device';
import { dispatchCustomEvent } from '../../base/package/util/event';
import styles from './ui-panic-button.css?inline';

const CName = 'ui-panic-button';

/**
 * @fires panicModeOn - when the panic button was pressed
 */
@customElement(CName)
export class UiPanicButton extends LitElement {
  static readonly styles = unsafeCSS(styles);
  @property() secondary: boolean;
  @property() text: string;
  @property() class: string;
  @property({ type: Number }) pressDurationS: number;

  private _progressIsRunning = false;

  @query('#progressBg') private readonly progressBg?: HTMLParagraphElement;
  @query('#button') private readonly buttonEl?: HTMLParagraphElement;

  render() {
    const classes = classMap({
      'ui-panic-button': true,
      'ui-panic-button--secondary': this.secondary,
      'ui-panic-button--mobile': isMobileWidth(),
    });
    return html`
      <button
        id="button"
        oncontextmenu="return false;"
        class=${classes}
        @pointerdown="${this._startProgress}"
        @pointerup="${this._stopProgress}"
        @pointerleave="${this._stopProgress}"
        @pointercancel="${this._stopProgress}"
        @touchmove="${this._handleTouchMove}"
        @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._kbStart.bind(this))}
        @keyup=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._stopProgress.bind(this))}
      >
        <div id="progressBg" class="ui-panic-button__progress"></div>
        <span class="ui-panic-button__text">${this.text.replace('{{seconds}}', String(this.pressDurationS))}</span>
      </button>
    `;
  }

  private _kbStart() {
    if (this._progressIsRunning) return;

    this._startProgress();
  }

  private _startProgress() {
    if (this._progressIsRunning) return;
    this._progressIsRunning = true;
    if (!this.progressBg) {
      window.$app.logger.error('progressBg element is not found', 'progressBg is undefined');
      return;
    }
    this.progressBg
      .animate([{ transform: 'translateX(-100%)' }, { transform: 'translateX(0)' }], this.pressDurationS * 1000)
      .finished.then(this._finishProgress.bind(this))
      .catch(() => {});
  }

  private _stopProgress() {
    if (!this._progressIsRunning) {
      return;
    }
    if (!this.progressBg) {
      window.$app.logger.error('progressBg element is not found', 'progressBg is undefined');
      return;
    }
    this.progressBg.getAnimations().forEach((a) => {
      a.cancel();
    });
    this._progressIsRunning = false;
  }

  private _finishProgress() {
    window.$app.track.panicButton(window.location.href);
    dispatchCustomEvent(this, 'panicModeOn');
    this._progressIsRunning = false;
  }

  private _handleTouchMove(event: TouchEvent) {
    if (!this.buttonEl) {
      window.$app.logger.error('Button element is not found', 'buttonEl is undefined');
      return;
    }

    const touch = event.touches[0];
    const rect = this.buttonEl.getBoundingClientRect();
    if (
      touch &&
      (touch.clientX < rect.left ||
        touch.clientX > rect.right ||
        touch.clientY < rect.top - 8 ||
        touch.clientY > rect.bottom + 16)
    ) {
      this._stopProgress();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UiPanicButton;
  }
}
